import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const SearchSidebar = ({
    searchInputRef,
    searchValue,
    setSearchValue,
    filters,
    setFilters,
    classTypeId,
    setClassTypeId,
    classTypeOptions,
    count,
    setCount,
    isPublic,
    setIsPublic,
    hasSound,
    setHasSound,
    sortBy,
    setSortBy,
    onSearch,
    activeTab,
    setActiveTab,
    categoryCounts
}) => {
    const { t } = useTranslation('common');
    const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onSearch();
        }
    };

    const handleCheckboxChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const selectedClassIds = classTypeId ? classTypeId.split(',').map(s => s.trim()).filter(Boolean) : [];

    const handleToggleClassType = (id) => {
        if (!id) {
            setClassTypeId('');
            return;
        }
        const strId = String(id);
        let next;
        if (selectedClassIds.includes(strId)) {
            next = selectedClassIds.filter(x => x !== strId);
        } else {
            next = [...selectedClassIds, strId];
        }
        setClassTypeId(next.join(','));
    };

    const filterCategories = [
        { key: 'IsLessons', tabKey: 'Lessons', label: t('PageHeader.Lessons', 'الدروس'), icon: 'fa-book-open', color: '#007bff' },
        { key: 'IsFiles', tabKey: 'Files', label: t('PageHeader.Files', 'الملفات'), icon: 'fa-paperclip', color: '#17a2b8' },
        { key: 'IsExams', tabKey: 'Exams', label: t('PageHeader.Exams', 'الامتحانات'), icon: 'fa-file-signature', color: '#dc3545' },
        { key: 'IsQuizzes', tabKey: 'Quizzes', label: t('PageHeader.Quizzes', 'الاختبارات'), icon: 'fa-question-circle', color: '#ffc107' },
        { key: 'IsFun', tabKey: 'Games', label: t('PageHeader.Fun', 'الألعاب'), icon: 'fa-gamepad', color: '#28a745' },
        { key: 'IsBooks', tabKey: 'Books', label: t('PageHeader.Books', 'الكتب'), icon: 'fa-book-bookmark', color: '#6f42c1' }
    ];

    const allSelected = filterCategories.every(c => filters[c.key] ?? true);

    const toggleAllFilters = () => {
        const targetValue = !allSelected;
        setFilters(prev => {
            const next = { ...prev };
            filterCategories.forEach(c => {
                next[c.key] = targetValue;
            });
            return next;
        });
    };

    return (
        <div
            className="search-hero-panel mb-3 p-3 text-right position-relative"
            style={{
                zIndex: 1000,
                background: 'rgba(255, 255, 255, 0.96)',
                backdropFilter: 'blur(16px)',
                borderRadius: '20px',
                boxShadow: '0 10px 30px rgba(146, 39, 143, 0.12), 0 4px 12px rgba(0, 0, 0, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.9)'
            }}
        >
            {/* Main Search Input Bar */}
            <div
                className="d-flex align-items-center bg-white p-1 rounded-pill shadow-sm mb-2"
                style={{
                    border: '2px solid #92278f',
                    boxShadow: '0 4px 15px rgba(146, 39, 143, 0.1)',
                    transition: 'all 0.3s ease'
                }}
            >
                <div className="pr-3 pl-2 text-primary">
                    <i className="fas fa-magnifying-glass font-size-h5"></i>
                </div>

                <input
                    onKeyDown={handleKeyDown}
                    ref={searchInputRef}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    autoComplete="off"
                    type="text"
                    className="form-control border-0 bg-transparent text-dark font-weight-bold shadow-none py-1"
                    placeholder={t('Pages.Search.input.Placeholder', 'ابحث هنا... (مثال: الفاعل، مهارات الاستماع، مادة العلوم)')}
                    style={{ fontSize: '1rem' }}
                />

                {searchValue && (
                    <button
                        type="button"
                        className="btn btn-link text-muted p-0 ml-2 border-0 shadow-none w-auto"
                        onClick={() => setSearchValue('')}
                        title={t('Pages.Search.ClearSearch', 'مسح')}
                    >
                        <i className="fas fa-times-circle font-size-h5"></i>
                    </button>
                )}

                <button
                    className="btn px-4 py-2 text-white font-weight-bold rounded-pill ml-1 transition-all w-auto"
                    style={{
                        background: 'linear-gradient(135deg, #92278f 0%, #b82ca5 100%)',
                        boxShadow: '0 4px 12px rgba(146, 39, 143, 0.3)',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        textWrap: 'nowrap'
                    }}
                    onClick={() => onSearch()}
                >
                    <i className="fas fa-search ml-1"></i>
                    {t('Pages.Search.submit', 'بحث')}
                </button>
            </div>

            {/* Advanced Search Controls Row */}
            <div className="d-flex align-items-center justify-content-between flex-wrap pt-2 pb-1 border-top border-bottom mb-2" style={{ borderColor: '#edf2f7' }}>
                <div className="d-flex align-items-center flex-wrap">
                    {/* ClassType Multi-Select Dropdown */}
                    {classTypeOptions && classTypeOptions.length > 0 && (
                        <div className="position-relative ml-3 mb-1" style={{ zIndex: 1050 }}>
                            <button
                                type="button"
                                className="btn btn-light rounded-pill px-3 py-1 border d-flex align-items-center font-weight-bold shadow-sm"
                                onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                                style={{
                                    height: '34px',
                                    fontSize: '0.85rem',
                                    backgroundColor: selectedClassIds.length > 0 ? '#f0e6f2' : '#f8fafc',
                                    borderColor: selectedClassIds.length > 0 ? '#92278f' : '#e2e8f0',
                                    color: selectedClassIds.length > 0 ? '#92278f' : '#495057',
                                    whiteSpace: 'nowrap',
                                    textWrap: 'nowrap'
                                }}
                            >
                                <i className="fas fa-graduation-cap ml-2 text-primary"></i>
                                {selectedClassIds.length === 0
                                    ? t("Pages.UsersControl.advancedSearch.ClassType.Placeholder", "كل الصفوف")
                                    : `${t('Pages.Search.SelectedClasses', 'الصفوف المحجوزة')}: (${selectedClassIds.length})`}
                                <i className="fas fa-chevron-down mr-2 small text-muted"></i>
                            </button>

                            {isClassDropdownOpen && (
                                <div
                                    className="bg-white rounded-lg p-2 shadow-lg text-right position-absolute"
                                    style={{
                                        top: '40px',
                                        right: 0,
                                        zIndex: 9999,
                                        borderRadius: '16px',
                                        minWidth: '240px',
                                        width: 'max-content',
                                        maxHeight: '280px',
                                        overflowY: 'auto',
                                        border: '1px solid #e2e8f0',
                                        whiteSpace: 'nowrap',
                                        textWrap: 'nowrap'
                                    }}
                                >
                                    <button
                                        className={`btn btn-block text-right font-weight-bold mb-1 btn-sm rounded-pill ${selectedClassIds.length === 0 ? 'btn-primary text-white' : 'btn-light text-dark'}`}
                                        type="button"
                                        style={{ whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                                        onClick={() => {
                                            handleToggleClassType('');
                                            setIsClassDropdownOpen(false);
                                        }}
                                    >
                                        <i className="fas fa-check-double ml-1"></i>
                                        {t("Pages.UsersControl.advancedSearch.ClassType.Placeholder", "كل الصفوف")}
                                    </button>
                                    <hr className="my-1" />
                                    {classTypeOptions.map(item => {
                                        const isSelected = selectedClassIds.includes(String(item.Id));
                                        return (
                                            <div
                                                key={item.Id}
                                                className={`d-flex align-items-center justify-content-between p-2 rounded cursor-pointer transition-all mb-1 ${isSelected ? 'bg-light font-weight-bold text-primary' : 'text-dark'}`}
                                                onClick={() => handleToggleClassType(item.Id)}
                                                style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                                            >
                                                <span style={{ whiteSpace: 'nowrap', textWrap: 'nowrap' }}>{item.Name}</span>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}}
                                                    className="mr-2"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Count Select */}
                    <div
                        className="d-flex align-items-center bg-light rounded-pill px-3 py-1 border ml-3 mb-1 transition-all"
                        style={{ height: '34px', borderColor: '#e2e8f0', backgroundColor: '#f8fafc', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                    >
                        <i className="fas fa-list-numeric text-primary ml-2 font-size-h6"></i>
                        <small className="text-muted font-weight-bold ml-2" style={{ whiteSpace: 'nowrap', textWrap: 'nowrap' }}>
                            {t('Pages.Search.ResultsCountSelect', 'عدد النتائج:')}
                        </small>
                        <select
                            value={count}
                            onChange={(e) => setCount(Number(e.target.value))}
                            className="border-0 bg-transparent text-dark font-weight-bold shadow-none"
                            style={{ cursor: 'pointer', outline: 'none', fontSize: '0.85rem', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>

                    {/* SortBy Select */}
                    <div
                        className="d-flex align-items-center bg-light rounded-pill px-3 py-1 border ml-3 mb-1 transition-all"
                        style={{ height: '34px', borderColor: '#e2e8f0', backgroundColor: '#f8fafc', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                    >
                        <i className="fas fa-arrow-down-wide-short text-primary ml-2 font-size-h6"></i>
                        <small className="text-muted font-weight-bold ml-2" style={{ whiteSpace: 'nowrap', textWrap: 'nowrap' }}>
                            {t('Pages.Search.SortByLabel', 'الترتيب:')}
                        </small>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="border-0 bg-transparent text-dark font-weight-bold shadow-none"
                            style={{ cursor: 'pointer', outline: 'none', fontSize: '0.85rem', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                        >
                            <option value="newest">{t('Pages.Search.SortNewest', 'الأحدث تاريخاً')}</option>
                            <option value="mostViewed">{t('Pages.Search.SortMostViewed', 'الأكثر مشاهدة')}</option>
                            <option value="topRated">{t('Pages.Search.SortTopRated', 'الأكثر تقييماً')}</option>
                        </select>
                    </div>

                    {/* IsSkills Toggle */}
                    <label
                        className={`d-inline-flex align-items-center rounded-pill px-3 py-1 border ml-2 mb-1 cursor-pointer transition-all ${
                            filters.IsSkills ? 'bg-warning text-dark border-warning shadow-sm font-weight-bold' : 'bg-light text-dark border-light'
                        }`}
                        style={{ height: '34px', fontSize: '0.82rem', userSelect: 'none', cursor: 'pointer', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                    >
                        <input
                            type="checkbox"
                            checked={filters.IsSkills}
                            onChange={(e) => handleCheckboxChange('IsSkills', e.target.checked)}
                            className="d-none"
                        />
                        <i className="fas fa-lightbulb ml-1"></i>
                        {t('Pages.Search.SearchBySkills', 'البحث بمطابقة المهارات')}
                    </label>

                    {/* IsPublic Toggle */}
                    <label
                        className={`d-inline-flex align-items-center rounded-pill px-3 py-1 border ml-2 mb-1 cursor-pointer transition-all ${
                            isPublic ? 'bg-primary text-white border-primary shadow-sm' : 'bg-light text-dark border-light'
                        }`}
                        style={{ height: '34px', fontSize: '0.82rem', userSelect: 'none', cursor: 'pointer', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                    >
                        <input
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            className="d-none"
                        />
                        <i className={`fas ${isPublic ? 'fa-lock-open' : 'fa-globe'} ml-1`}></i>
                        {t('Pages.Search.PublicOnly', 'عامة فقط')}
                    </label>

                    {/* HasSound Toggle */}
                    <label
                        className={`d-inline-flex align-items-center rounded-pill px-3 py-1 border ml-2 mb-1 cursor-pointer transition-all ${
                            hasSound ? 'bg-info text-white border-info shadow-sm' : 'bg-light text-dark border-light'
                        }`}
                        style={{ height: '34px', fontSize: '0.82rem', userSelect: 'none', cursor: 'pointer', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                    >
                        <input
                            type="checkbox"
                            checked={hasSound}
                            onChange={(e) => setHasSound(e.target.checked)}
                            className="d-none"
                        />
                        <i className={`fas ${hasSound ? 'fa-volume-high' : 'fa-volume-xmark'} ml-1`}></i>
                        {t('Pages.Search.HasSound', 'تحتوي على صوت')}
                    </label>
                </div>

                <button
                    type="button"
                    className="btn btn-link btn-sm text-muted p-0 mb-1 small font-weight-bold border-0 shadow-none w-auto"
                    onClick={toggleAllFilters}
                    style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', textWrap: 'nowrap' }}
                >
                    <i className={`fas ${allSelected ? 'fa-eye-slash' : 'fa-check-double'} ml-1`}></i>
                    {allSelected ? t('Pages.Search.DeselectAllCategories', 'إلغاء تحديد الأقسام') : t('Pages.Search.SelectAllCategories', 'تحديد كل الأقسام')}
                </button>
            </div>

            {/* Compact Filter Chips Row */}
            <div className="d-flex align-items-center justify-content-between flex-wrap pt-1">
                <div className="d-flex align-items-center flex-wrap">
                    <button
                        type="button"
                        onClick={() => setActiveTab && setActiveTab('all')}
                        className={`btn btn-sm rounded-pill ml-1 mb-1 transition-all font-weight-bold w-auto ${
                            (!activeTab || activeTab === 'all')
                                ? 'btn-primary text-white shadow-sm'
                                : 'btn-light text-dark'
                        }`}
                        style={{
                            padding: '4px 12px',
                            fontSize: '0.8rem',
                            backgroundColor: (!activeTab || activeTab === 'all') ? '#92278f' : '#f1f3f5',
                            border: (!activeTab || activeTab === 'all') ? '1px solid #92278f' : '1px solid #e9ecef',
                            whiteSpace: 'nowrap',
                            textWrap: 'nowrap'
                        }}
                    >
                        <i className="fas fa-th-large ml-1"></i>
                        {t('Pages.Search.AllCategories', 'الكل')}
                    </button>

                    {filterCategories.map(item => {
                        const isChecked = filters[item.key] ?? true;
                        const count = categoryCounts ? categoryCounts[item.tabKey] : null;
                        const isCurrentTab = activeTab === item.tabKey;

                        return (
                            <button
                                key={item.key}
                                type="button"
                                onClick={() => {
                                    if (!isChecked) {
                                        handleCheckboxChange(item.key, true);
                                    }
                                    if (setActiveTab) {
                                        setActiveTab(isCurrentTab ? 'all' : item.tabKey);
                                    }
                                }}
                                className="btn btn-sm rounded-pill ml-1 mb-1 transition-all font-weight-bold d-flex align-items-center w-auto"
                                style={{
                                    backgroundColor: isChecked ? (isCurrentTab ? item.color : `${item.color}15`) : '#f8f9fa',
                                    color: isChecked ? (isCurrentTab ? '#ffffff' : item.color) : '#6c757d',
                                    border: `1px solid ${isChecked ? item.color : '#e9ecef'}`,
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    whiteSpace: 'nowrap',
                                    textWrap: 'nowrap'
                                }}
                            >
                                <i className={`fas ${item.icon} ml-1`}></i>
                                {item.label}
                                {count !== null && count !== undefined && (
                                    <span
                                        className="badge badge-pill mr-1 font-weight-bold"
                                        style={{
                                            backgroundColor: isCurrentTab ? 'rgba(255,255,255,0.3)' : item.color,
                                            color: '#ffffff',
                                            fontSize: '0.7rem'
                                        }}
                                    >
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SearchSidebar;
